// Storage service (TypeScript)

import { AppSettings, Favorite, WorklogJSON } from '../types/storage';

const DEFAULT_SETTINGS: AppSettings = {
  lang: 'en',
  autoSync: false,
  theme: 'light'
};

interface ImportResult {
  settings?: AppSettings;
  worklogs?: WorklogJSON[];
  favorites?: Favorite[];
}

export class StorageService {
  static get authTokenKey(): string {
    return 'tf_auth_token';
  }

  static get settingsKey(): string {
    return 'tf_settings';
  }

  static get worklogsKey(): string {
    return 'tf_worklogs';
  }

  static get favoritesKey(): string {
    return 'tf_favorites';
  }

  static reset(): void {
    localStorage.setItem(StorageService.authTokenKey, '');
    localStorage.setItem(StorageService.settingsKey, JSON.stringify({}));
    localStorage.setItem(StorageService.worklogsKey, JSON.stringify([]));
    localStorage.setItem(StorageService.favoritesKey, JSON.stringify([]));
  }

  static loadAuthToken(): string {
    const token = localStorage.getItem(StorageService.authTokenKey);
    return token && token.length > 0 ? token : '';
  }

  static saveAuthToken(token: string): void {
    localStorage.setItem(StorageService.authTokenKey, token);
  }

  static loadSettings(): AppSettings {
    const raw = localStorage.getItem(StorageService.settingsKey);
    if (!raw) return { ...DEFAULT_SETTINGS };

    try {
      const s = JSON.parse(raw);
      const parsed: any = {
        lang: s.lang || DEFAULT_SETTINGS.lang,
        autoSync: !!s.autoSync,
        theme: s.theme === 'light' ? 'light' : 'dark'
      } as AppSettings;
      return parsed;
    } catch {
      return { ...DEFAULT_SETTINGS };
    }
  }

  static saveSettings(s: AppSettings): void {
    const withTheme: AppSettings = {
      ...s,
      theme: s.theme === 'light' ? 'light' : 'dark'
    };
    localStorage.setItem(StorageService.settingsKey, JSON.stringify(withTheme));
  }

  static loadWorklogs(): WorklogJSON[] {
    const raw = localStorage.getItem(StorageService.worklogsKey);
    if (!raw) return [];

    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }

  static saveWorklogs(arr: WorklogJSON[]): void {
    localStorage.setItem(StorageService.worklogsKey, JSON.stringify(arr));
  }

  static loadFavorites(): Favorite[] {
    const raw = localStorage.getItem(StorageService.favoritesKey);
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  }

  static saveFavorites(favs: Favorite[]): void {
    localStorage.setItem(StorageService.favoritesKey, JSON.stringify(favs));
  }

  static exportAll(): string {
    return JSON.stringify({
      version: 1,
      settings: StorageService.loadSettings(),
      worklogs: StorageService.loadWorklogs(),
      favorites: StorageService.loadFavorites()
    }, null, 2);
  }

  // TODO: move to import/export service?
  static importAll(json: string): ImportResult {
    let data: any;
    try {
      data = JSON.parse(json);
    } catch {
      throw new Error('Invalid JSON');
    }

    if (!data || typeof data !== 'object') {
      throw new Error('Invalid JSON');
    }

    const res: ImportResult = {};

    if (data.settings) {
      const settings = StorageService.loadSettings();

      res.settings = {
        lang: typeof data.settings.lang === 'string' ? data.settings.lang : settings.lang,
        autoSync: typeof data.settings.autoSync === 'boolean' ? data.settings.autoSync : settings.autoSync,
        theme: data.settings.theme === 'light' ? 'light' : 'dark'
      };
    }

    if (Array.isArray(data.worklogs)) {
      const worklogs = StorageService.loadWorklogs();
      const existingIds = new Set(worklogs.map((w) => w.id));

      // remove duplicates based on id (if any)
      const newWorklogs = data.worklogs.filter((w: any) => w.id && !existingIds.has(w.id));

      res.worklogs = worklogs.concat(newWorklogs);
    }

    if (Array.isArray(data.favorites)) {
      const favorites = StorageService.loadFavorites();
      const existingIds = new Set(favorites.map((f) => f.id));

      // remove duplicates based on id (if any)
      const newFavorites = data.favorites.filter((f: any) => f.id && !existingIds.has(f.id));

      res.favorites = favorites.concat(newFavorites);
    }

    return res;
  }
}